import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Sparkles, CheckSquare, DollarSign, CalendarDays, List, ListChecks,
  Mail, MapPin, Hash, Phone, Type, CircleDot, Link as LinkIcon, Calculator,
  X,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { TRAFFIC_LIGHT_DEFAULT_LABELS } from "@/lib/utils/custom-column";
import { genericFormulaFailure, useFormulaGeneration } from "@/lib/hooks/use-formula-generation";
import {
  DATE_TYPES,
  EMPTY_IF,
  FORMULA_KINDS,
  FORMULA_OPERATORS,
  NUMERIC_TYPES,
  formulaKindHelp,
  formulaPlaceholder,
  formulaRelationLabel,
  formulaSegments,
  formulaTokenLabel,
  inferFormulaResultType,
  isVariadicKind,
  parseFormula,
  pickFormulaFields,
  resolveFormulaField,
  resolveFormulaState,
  serializeFormula,
  serializeIf,
  withTodayOption,
  STAGE_FIELD,
  type FormulaCondition,
  type FormulaFieldOption,
  type FormulaKind,
  type FormulaLookupRelation,
  type FormulaResultType,
  type FormulaSlots,
  type IfFormula,
} from "@/lib/utils/formula-fields";

// The formula grammar lives in `@/lib/utils/formula-fields` (pure + unit
// tested). Re-exported here so existing importers of this module keep working.
export {
  DATE_TYPES, EMPTY_IF, FORMULA_KINDS, FORMULA_OPERATORS, NUMERIC_TYPES,
  formulaSegments, parseFormula, pickFormulaFields, serializeFormula, serializeIf,
} from "@/lib/utils/formula-fields";
export type {
  FormulaCondition, FormulaFieldOption, FormulaKind, FormulaLookupRelation,
  FormulaResultType, FormulaSlots, FormulaState, IfFormula,
} from "@/lib/utils/formula-fields";

export type CustomFieldType =
  | "text" | "number" | "currency" | "date" | "email" | "phone" | "url"
  | "location" | "single-select" | "multi-select" | "checkbox"
  | "traffic-light" | "person" | "company" | "relation" | "rich-text" | "ai"
  | "formula";

/** Swatch colours for the traffic-light labels, in the stored order. */
const TRAFFIC_LIGHT_COLORS = ["bg-green-500", "bg-yellow-500", "bg-red-500"] as const;

export type CustomFieldObjectKey = "people" | "companies" | "deals" | "notes" | "tasks";

/**
 * `type` is the value written to the backend's `custom_field_type` enum and
 * must never be translated; `label` stays as the English identity (it is what
 * the alphabetical order and the un-keyed fallback are built on) and
 * `labelKey` is what the reader actually sees.
 */
export const FIELD_TYPE_OPTIONS: {
  type: CustomFieldType; label: string; labelKey: string; icon: React.ElementType;
}[] = [
  // Kept in alphabetical order by label so the type dropdown is easy to scan.
  { type: "ai", label: "AI", labelKey: "forms.fieldType.ai", icon: Sparkles },
  { type: "checkbox", label: "Checkbox", labelKey: "forms.fieldType.checkbox", icon: CheckSquare },
  { type: "currency", label: "Currency", labelKey: "forms.fieldType.currency", icon: DollarSign },
  { type: "date", label: "Date", labelKey: "forms.fieldType.date", icon: CalendarDays },
  { type: "email", label: "Email", labelKey: "forms.fieldType.email", icon: Mail },
  { type: "formula", label: "Formula", labelKey: "forms.fieldType.formula", icon: Calculator },
  { type: "location", label: "Location", labelKey: "forms.fieldType.location", icon: MapPin },
  { type: "multi-select", label: "Multi-Select", labelKey: "forms.fieldType.multiSelect", icon: ListChecks },
  { type: "number", label: "Number", labelKey: "forms.fieldType.number", icon: Hash },
  { type: "phone", label: "Phone", labelKey: "forms.fieldType.phone", icon: Phone },
  { type: "single-select", label: "Single Select", labelKey: "forms.fieldType.singleSelect", icon: List },
  { type: "text", label: "Text", labelKey: "forms.fieldType.text", icon: Type },
  { type: "traffic-light", label: "Traffic Light", labelKey: "forms.fieldType.trafficLight", icon: CircleDot },
  { type: "url", label: "URL", labelKey: "forms.fieldType.url", icon: LinkIcon },
];

/** The `t` from `useTranslation()`, narrowed to what this module calls. */
type Translator = (key: string) => string;

/**
 * Display label for a field type. A type the picker does not know about is a
 * backend value we have no copy for, so it falls back to a title-cased version
 * of the raw value rather than to English copy.
 */
export function typeLabel(type: string, t: Translator): string {
  const option = FIELD_TYPE_OPTIONS.find(o => o.type === type);
  if (option) return t(option.labelKey);
  return type.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

export function typeIcon(type: string): React.ElementType {
  return FIELD_TYPE_OPTIONS.find(o => o.type === type)?.icon ?? Type;
}

export function TypeBadge({ type }: { type: string }) {
  const { t } = useTranslation();
  const Icon = typeIcon(type);
  const label = typeLabel(type, t);
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon className="h-3.5 w-3.5 shrink-0" />
            <span>{label}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Clickable variable chip inside the formula expression — opens a field picker.
 *
 * `value` is the field's canonical key (what goes into the stored expression);
 * the chip shows the field's label. A key that matches no available field —
 * the field was deleted, or the formula was authored before expressions stored
 * keys — renders as a broken chip and blocks submitting.
 */
function FormulaFieldToken({
  value, placeholder, fields, onSelect, emptyLabel,
}: { value: string; placeholder: string; fields: FormulaFieldOption[]; onSelect: (v: string) => void; emptyLabel?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const missing = !!value && resolveFormulaField(value, fields).status === "missing";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={missing ? t("forms.customField.missingFieldTitle", { field: value }) : undefined}
          className={cn(
            "inline-flex items-center rounded px-1.5 py-0.5 mx-0.5 text-xs border transition",
            missing
              ? "bg-destructive/10 border-destructive/50 text-destructive line-through"
              : value
                ? "bg-accent/15 border-accent/40 text-foreground"
                : "bg-muted/50 border-dashed border-border text-muted-foreground"
          )}
        >
          {value ? formulaTokenLabel(value, fields) : placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64" align="start">
        <Command>
          <CommandInput placeholder={t("forms.customField.searchFields")} className="text-sm" />
          <CommandList className="max-h-56">
            <CommandEmpty>{emptyLabel || t("forms.customField.noFieldsFound")}</CommandEmpty>
            <CommandGroup>
              {/* Hidden options stay resolvable (so a formula that uses an
                  alias key still opens) but are never offered. */}
              {fields.filter(f => !f.hidden).map(f => (
                <CommandItem
                  key={f.key}
                  value={f.label}
                  onSelect={() => { onSelect(f.key); setOpen(false); }}
                  className="text-sm"
                >
                  {f.label}
                  <span className="ml-auto text-[10px] text-muted-foreground">{typeLabel(f.type, t)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Inline free-text token (operators, literals, then/else results). */
function FormulaValueToken({
  value, placeholder, onChange,
}: { value: string; placeholder: string; onChange: (v: string) => void }) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      size={Math.max((value || placeholder).length, 3)}
      className="inline-block rounded px-1.5 py-0.5 mx-0.5 text-xs border border-dashed border-border bg-muted/50 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
    />
  );
}


/**
 * Starter AI fields. `labelKey` is UI copy; `prompt` is the instruction the AI
 * runs and stays English — it is the content of the user-owned prompt field,
 * not app chrome.
 */
export const AI_SUGGESTIONS: Partial<Record<CustomFieldObjectKey, { labelKey: string; prompt: string }[]>> = {
  people: [
    { labelKey: "forms.aiSuggestion.buyerFit", prompt: "Assess how well this person fits our buyer profile based on their title, seniority, function, and company. Return a fit score (Strong, Moderate, Weak) with a 1-2 sentence rationale." },
    { labelKey: "forms.aiSuggestion.buyingRole", prompt: "Classify this person's buying role (e.g. Economic Buyer, Champion, Decision Maker, Influencer, End User) based on their title, seniority, and function. Return just the role name." },
    { labelKey: "forms.aiSuggestion.personEngagement", prompt: "Summarize this person's recent engagement with us across meetings, emails, and activity. Highlight the level of activity and any notable signals in 1-2 sentences." },
    { labelKey: "forms.aiSuggestion.department", prompt: "Identify this person's department (e.g. Engineering, Sales, Marketing, Finance, Operations, HR) based on their title and function. Return just the department name." },
  ],
  companies: [
    { labelKey: "forms.aiSuggestion.accountFit", prompt: "Assess how well this company fits our ICP based on industry, size, go-to-market model, and tech stack. Return a fit score (Strong, Moderate, Weak) with a 1-2 sentence rationale." },
    { labelKey: "forms.aiSuggestion.recentNews", prompt: "Summarize the most notable recent news about this company from the last 6 months (funding, product launches, exec hires, layoffs) in 1-2 sentences." },
    { labelKey: "forms.aiSuggestion.companyEngagement", prompt: "Summarize this company's recent engagement with us across meetings, emails, and product usage. Highlight the level of activity and any notable signals in 1-2 sentences." },
  ],
  deals: [
    { labelKey: "forms.aiSuggestion.nextBestAction", prompt: "Based on this deal's stage, activity, and stakeholders, recommend the single next best action the seller should take. Be specific and concise." },
    { labelKey: "forms.aiSuggestion.riskAssessment", prompt: "Assess the risk level of this deal (Low, Medium, High) and give a one-sentence rationale based on stage duration, engagement, and missing MEDDPICC fields." },
    { labelKey: "forms.aiSuggestion.championStatus", prompt: "Identify whether this deal has a confirmed Champion. Return one of: Confirmed, Suspected, None — followed by a short rationale." },
    { labelKey: "forms.aiSuggestion.knownCompetitors", prompt: "List any known competitors involved in this deal based on notes, emails, and meeting transcripts. Return a comma-separated list, or 'None identified' if no competitors are mentioned." },
  ],
};

export interface CustomFieldFormProps {
  mode: "add" | "edit";
  label: string; setLabel: (v: string) => void;
  type: CustomFieldType; setType: (v: CustomFieldType) => void;
  description?: string; setDescription?: (v: string) => void;
  options: string[]; setOptions: (v: string[]) => void;
  aiPrompt: string; setAiPrompt: (v: string) => void;
  /** Formula source (Excel-style). Optional — callers that don't support formula fields can omit. */
  formula?: string;
  setFormula?: (v: string) => void;
  /** Fields selectable inside the formula builder (standard + custom for this object). */
  formulaFields?: FormulaFieldOption[];
  /**
   * Relations a LOOKUP can traverse, each with the fields available on the far
   * side. Omit (or pass an empty list) and LOOKUP is hidden from the picker.
   */
  lookupRelations?: FormulaLookupRelation[];
  /**
   * Deal stages as `{value: externalId, label: name}`, for IF conditions on `stage`.
   *
   * A deal record stores the stage's opaque external id, not its name, so a formula
   * comparing `stage` to a typed-in name saves and then never matches — the backend
   * rejects it outright. Passed in rather than fetched here so this shared form keeps no
   * network hook of its own; omit it and the value stays a free-text input.
   */
  stageOptions?: { value: string; label: string }[];
  /**
   * Reports what the formula will evaluate to, so the caller can persist it
   * alongside the expression (it decides which OpenSearch subfield the column
   * sorts on).
   */
  onFormulaResultTypeChange?: (t: FormulaResultType) => void;
  /**
   * Backend object type ("Contact" | "Account" | "Deal") for natural-language
   * formula generation. Without it the builder falls back to local keyword
   * interpretation.
   */
  formulaObjectType?: string | null;
  /** Restrict the type select to a subset (e.g. omit `person`/`company`/`relation`/`rich-text` in the grid). */
  allowedTypes?: CustomFieldType[];
  /** Disable specific type options (e.g. "ai" while research is in progress). */
  disabledTypes?: Partial<Record<CustomFieldType, string>>;
  /**
   * Lovable-preview only: the mocked "Run on list" picker. In production the
   * Data Model page collects scope + credits in AiFieldRunConfirm instead.
   */
  showAiRunOnList?: boolean;
  objectKey?: CustomFieldObjectKey;
  submitLabel?: string;
  submitDisabled?: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

export default function CustomFieldForm({
  mode, label, setLabel, type, setType,
  description, setDescription,
  options, setOptions, aiPrompt, setAiPrompt,
  formula = "", setFormula,
  formulaFields = [],
  lookupRelations = [],
  stageOptions = [],
  onFormulaResultTypeChange,
  formulaObjectType,
  allowedTypes, disabledTypes, showAiRunOnList = false,
  objectKey, submitLabel, submitDisabled, onCancel, onSubmit,
}: CustomFieldFormProps) {
  const { t } = useTranslation();
  const needsOptions = type === "single-select" || type === "multi-select";
  const isAi = type === "ai";
  const isFormula = type === "formula";
  const isTrafficLight = type === "traffic-light";
  const [aiListId, setAiListId] = useState<string>("all");
  const parsed = parseFormula(formula);
  const [fKind, setFKind] = useState<FormulaKind>(parsed.kind);
  const [fSlots, setFSlots] = useState<FormulaSlots>(parsed.slots);
  const [fArgs, setFArgs] = useState<string[]>(parsed.args.length ? parsed.args : [""]);
  const [ifState, setIfState] = useState<IfFormula>(parsed.ifState ?? EMPTY_IF);
  // Natural-language entry: describe the formula, then refine it in the
  // guided builder. Existing formula fields open straight into the builder.
  const [formulaMode, setFormulaMode] = useState<"describe" | "manual">(
    mode === "add" && !formula.trim() ? "describe" : "manual",
  );
  const [nlText, setNlText] = useState("");
  const [nlResult, setNlResult] = useState<{ summary: string; gaps: string[] } | null>(null);
  const [nlError, setNlError] = useState<string | null>(null);
  const { generate: generateFormula, generating: generatingFormula } = useFormulaGeneration();
  const aiListOptions = [
    { id: "all", label: "All records" },
    { id: "list_target_accounts", label: "Target Accounts" },
    { id: "list_enterprise_leads", label: "Enterprise Leads" },
    { id: "list_q4_pipeline", label: "Q4 Pipeline" },
    { id: "list_recent_signups", label: "Recent Signups" },
  ];

  useEffect(() => {
    if (needsOptions && options.length === 0) {
      setOptions([""]);
    }
  }, [needsOptions, options, setOptions]);

  // Re-sync the builder when the formula is loaded/changed from outside (e.g.
  // opening the edit form on an existing formula field). Field references are
  // resolved to canonical keys on the way in, so a formula authored before
  // expressions stored keys (labels, from Lovable preview) opens correctly.
  useEffect(() => {
    if (serializeFormula(fKind, fSlots, ifState, fArgs) === formula) return;
    if (!formula.trim()) return;
    const p = parseFormula(formula);
    const relationFields = lookupRelations.find(r => r.relation === p.slots.a)?.fields ?? [];
    const { state } = resolveFormulaState(p, formulaFields, relationFields);
    setFKind(state.kind);
    setFSlots(state.slots);
    setFArgs(state.args.length ? state.args : [""]);
    if (state.ifState) setIfState(state.ifState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formula, formulaFields, lookupRelations]);

  // Seed traffic-light labels with the defaults so users can immediately tweak
  // them. Persisted as options in the fixed order [green, yellow, red].
  useEffect(() => {
    if (isTrafficLight && options.length < 3) {
      const next = [0, 1, 2].map(i => (options[i] && options[i].trim()) || TRAFFIC_LIGHT_DEFAULT_LABELS[i]);
      setOptions(next);
    }
  }, [isTrafficLight, options, setOptions]);

  const validOptionCount = options.filter(o => o.trim()).length;

  // Example prompts are derived from the fields that actually exist on this
  // object — no deal-specific suggestions on People/Companies. If the object
  // has nothing worth calculating, no chips render at all.
  const nlExamples = (() => {
    if (!isFormula) return [] as string[];
    const numeric = pickFormulaFields(formulaFields, NUMERIC_TYPES);
    const dates = pickFormulaFields(formulaFields, DATE_TYPES);
    const out: string[] = [];
    if (numeric[0]) out.push(`Average ${numeric[0].label.toLowerCase()}`);
    if (numeric[0]) out.push(`Total ${numeric[0].label.toLowerCase()}`);
    if (dates.length >= 2) out.push(`Days between ${dates[0].label.toLowerCase()} and ${dates[1].label.toLowerCase()}`);
    if (numeric[0]) out.push(`If ${numeric[0].label.toLowerCase()} is greater than 100 then 1 else 0`);
    return out.slice(0, 4);
  })();

  // Fields available on the far side of the relation a LOOKUP is pointed at.
  const activeRelation = lookupRelations.find(r => r.relation === fSlots.a);
  const lookupTargetFields = activeRelation?.fields ?? [];

  // The builder is the single source of truth for the formula string; keep the
  // parent in sync so the submit button reflects what's on screen.
  const builtFormula = isFormula ? serializeFormula(fKind, fSlots, ifState, fArgs) : "";
  useEffect(() => {
    if (!isFormula) return;
    if (builtFormula !== formula) setFormula?.(builtFormula);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormula, builtFormula]);

  // Tell the caller what this evaluates to, so it can be stored next to the
  // expression and drive the column's sort subfield.
  const formulaResultType: FormulaResultType = inferFormulaResultType(fKind, {
    ifState,
    // The whole option, not just its type: when the target is another formula
    // field, only its `resultType` says what the LOOKUP actually returns.
    lookupTarget: lookupTargetFields.find(f => f.key === fSlots.b),
  });
  useEffect(() => {
    if (!isFormula) return;
    onFormulaResultTypeChange?.(formulaResultType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormula, formulaResultType]);

  const applyFormula = (
    kind: FormulaKind,
    slots: FormulaSlots,
    nextIf: IfFormula = ifState,
    args: string[] = fArgs,
  ) => {
    setFKind(kind); setFSlots(slots); setIfState(nextIf); setFArgs(args);
    setFormula?.(serializeFormula(kind, slots, nextIf, args));
  };
  const setSlot = (slot: string, value: string) => applyFormula(fKind, { ...fSlots, [slot]: value });
  const setArg = (i: number, value: string) =>
    applyFormula(fKind, fSlots, ifState, fArgs.map((a, idx) => (idx === i ? value : a)));
  const addArg = () => applyFormula(fKind, fSlots, ifState, [...fArgs, ""]);
  const removeArg = (i: number) =>
    applyFormula(fKind, fSlots, ifState, fArgs.filter((_, idx) => idx !== i));
  /** Switching relation invalidates the field picked on the old one. */
  const setLookupRelation = (relation: string) => applyFormula(fKind, { a: relation, b: "" });
  const applyIf = (next: Partial<IfFormula>) => applyFormula("if", fSlots, { ...ifState, ...next });
  const setCond = (i: number, patch: Partial<FormulaCondition>) =>
    applyIf({ conds: ifState.conds.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });

  const runInterpret = async () => {
    let result: Awaited<ReturnType<typeof generateFormula>> = null;
    try {
      result = await generateFormula({
        description: nlText,
        objectType: formulaObjectType,
        fields: formulaFields,
        lookupFields: lookupTargetFields,
      });
    } catch {
      result = null;
    }
    // Stay in "describe" mode on failure and say what went wrong. Switching to
    // the builder with nothing filled in was the bug: an untouched AVERAGE looked
    // like a formula we had half-built, with no hint that the description was the
    // problem.
    if (result === null) {
      setNlResult(null);
      setNlError(genericFormulaFailure());
      return;
    }
    if (result.status === "unsupported") {
      setNlResult(null);
      setNlError(result.message);
      return;
    }
    setNlError(null);
    setNlResult({ summary: result.summary ?? "", gaps: result.gaps });
    const { state } = result;
    const args = isVariadicKind(state.kind)
      ? (state.args.length ? state.args : [state.slots.a ?? ""])
      : [""];
    applyFormula(state.kind, state.slots, state.ifState ?? EMPTY_IF, args);
    setFormulaMode("manual");
  };

  // A reference the object no longer has must not be persisted — the backend
  // would evaluate it to nothing and the column would silently read empty.
  const unresolvedRefs = isFormula
    ? resolveFormulaState(
        { kind: fKind, slots: fSlots, args: fArgs, ifState },
        formulaFields,
        lookupTargetFields,
      ).unresolved
    : [];
  const formulaIsComplete =
    !isFormula || (!!(builtFormula || formula).trim() && unresolvedRefs.length === 0);
  const formIsValid = (
    !!label.trim() &&
    (!needsOptions || validOptionCount > 0) &&
    (!isAi || !!aiPrompt.trim()) &&
    formulaIsComplete
  );
  // `submitDisabled` is an additional busy/permission guard, not a replacement
  // for form validation. Accept the synchronized parent value as well as the
  // freshly built value so a completed expression enables Create immediately.
  const canSubmit = formIsValid && !submitDisabled;

  const typeItems = FIELD_TYPE_OPTIONS.filter(o => !allowedTypes || allowedTypes.includes(o.type));

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.fieldName")}</label>
        <Input
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder={t("forms.customField.fieldNamePlaceholder")}
          autoFocus
          onKeyDown={e => { if (e.key === "Enter" && canSubmit) onSubmit(); }}
        />
      </div>
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">{t("common.type")}</label>
        <Select value={type} onValueChange={(v) => setType(v as CustomFieldType)}>
          {/* Stable hook for the E2E formula specs — they used to pick this
              positionally (getByRole("combobox").first()), which any added Select
              would have silently repointed. */}
          <SelectTrigger className="h-9 text-sm" data-testid="field-type-select">
            <SelectValue placeholder={t("forms.customField.selectType")} />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {typeItems.map(opt => {
              const Icon = opt.icon;
              const disabledReason = disabledTypes?.[opt.type];
              return (
                <SelectItem
                  key={opt.type}
                  value={opt.type}
                  className="text-sm"
                  disabled={!!disabledReason}
                >
                  <span className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{t(opt.labelKey)}</span>
                    {disabledReason && (
                      <span className="ml-auto text-[10px] text-muted-foreground">{disabledReason}</span>
                    )}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
      {isAi && (
        <>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.prompt")}</label>
            <Textarea
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
              placeholder={t("forms.customField.promptPlaceholder")}
              rows={3}
              className="text-sm resize-none"
            />
          </div>
          {mode === "add" && objectKey && AI_SUGGESTIONS[objectKey] && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.suggestions")}</label>
              <div className="flex flex-wrap gap-1.5">
                {AI_SUGGESTIONS[objectKey]!.map(s => (
                  <button
                    key={s.labelKey}
                    type="button"
                    onClick={() => { setLabel(t(s.labelKey)); setAiPrompt(s.prompt); }}
                    className="text-xs px-2 py-1 rounded-full border border-border bg-muted/40 hover:bg-muted text-foreground transition"
                  >
                    {t(s.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {showAiRunOnList && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.runOnList")}</label>
              <Select value={aiListId} onValueChange={setAiListId}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder={t("forms.customField.selectList")} />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {aiListOptions.map(l => (
                    <SelectItem key={l.id} value={l.id} className="text-sm">
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground pt-1">
                {t("forms.customField.creditsPerRow")}
              </p>
            </div>
          )}
        </>
      )}
      {isFormula && formulaMode === "describe" && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.describeCalculation")}</label>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              onClick={() => { setNlError(null); setFormulaMode("manual"); }}
            >
              {t("forms.customField.buildManually")}
            </button>
          </div>
          <Textarea
            value={nlText}
            onChange={e => setNlText(e.target.value)}
            placeholder={nlExamples[0]
              ? t("forms.customField.describePlaceholder", { example: nlExamples[0] })
              : t("forms.customField.describePlaceholderDefault")}
            rows={3}
            className="text-sm resize-none"
          />
          {nlExamples.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {nlExamples.map(ex => (
              <button
                key={ex}
                type="button"
                onClick={() => setNlText(ex)}
                className="text-xs px-2 py-1 rounded-full border border-border bg-muted/40 hover:bg-muted text-foreground transition"
              >
                {ex}
              </button>
            ))}
          </div>
          )}
          {nlError && <p className="text-xs text-destructive">{nlError}</p>}
          <Button
            type="button" size="sm" className="h-8 text-xs"
            disabled={!nlText.trim() || generatingFormula}
            onClick={runInterpret}
          >
            {generatingFormula ? t("forms.customField.generating") : t("forms.customField.generateFormula")}
          </Button>
          <p className="text-xs text-muted-foreground">
            {t("forms.customField.generateHelp")}
          </p>
        </div>
      )}
      {isFormula && formulaMode === "manual" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">{t("forms.customField.guidedBuilder")}</span>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              onClick={() => { setNlError(null); setFormulaMode("describe"); }}
            >
              {t("forms.customField.describePlain")}
            </button>
          </div>
          {nlResult && nlResult.gaps.length > 0 && (
            <div className="rounded-md border border-border bg-muted/30 px-2 py-1.5">
              <ul className="text-xs text-muted-foreground list-disc pl-4">
                {nlResult.gaps.map((g, i) => <li key={i}>{g}</li>)}
              </ul>
            </div>
          )}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.formula")}</label>
            <Select
              value={fKind}
              onValueChange={(v) => applyFormula(v as FormulaKind, {}, EMPTY_IF, [""])}
            >
              {/* See field-type-select above: the E2E specs addressed this one as
                  getByRole("combobox").nth(1). */}
              <SelectTrigger className="h-9 text-sm" data-testid="formula-kind-select">
                <SelectValue placeholder={t("forms.customField.selectFormula")} />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {FORMULA_KINDS
                  // LOOKUP needs somewhere to look: hide it on objects with no
                  // relations rather than offering a dead end. Every
                  // formula-capable object has an `owner` relation, so in
                  // practice this only fires for a caller that passes none.
                  .filter(k => k.kind !== "lookup" || lookupRelations.length > 0)
                  .map(k => (
                    <SelectItem key={k.kind} value={k.kind} className="text-sm">{k.label}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground pt-0.5">
              {formulaKindHelp(fKind, t)}
            </p>
          </div>
          {fKind === "if" ? (
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.conditions")}</label>
              <div className="space-y-1.5">
                {ifState.conds.map((c, i) => (
                  <div key={i} className="flex items-center gap-1.5 flex-wrap">
                    {i > 0 && (
                      <Select value={ifState.join} onValueChange={(v) => applyIf({ join: v as "AND" | "OR" })}>
                        <SelectTrigger className="h-8 w-[74px] text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AND" className="text-xs">AND</SelectItem>
                          <SelectItem value="OR" className="text-xs">OR</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {i === 0 && <span className="text-xs text-muted-foreground w-[74px]">{t("forms.customField.if")}</span>}
                    <FormulaFieldToken
                      value={c.field}
                      placeholder={t("forms.customField.fieldPlaceholder")}
                      fields={formulaFields}
                      onSelect={(v) => setCond(i, { field: v })}
                    />
                    <Select value={c.op} onValueChange={(v) => setCond(i, { op: v })}>
                      <SelectTrigger className="h-8 w-[68px] text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FORMULA_OPERATORS.map(op => (
                          <SelectItem key={op} value={op} className="text-xs">{op}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {c.field === STAGE_FIELD && stageOptions.length > 0 ? (
                      // Names shown, ids stored — the record holds the id, and a typed
                      // name is rejected by the backend.
                      <Select value={c.value} onValueChange={(v) => setCond(i, { value: v })}>
                        <SelectTrigger className="h-8 text-xs flex-1 min-w-[100px]">
                          <SelectValue placeholder={t("forms.customField.stagePlaceholder")} />
                        </SelectTrigger>
                        <SelectContent>
                          {stageOptions.map(s => (
                            <SelectItem key={s.value} value={s.value} className="text-xs">
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={c.value}
                        onChange={e => setCond(i, { value: e.target.value })}
                        placeholder={t("forms.customField.valuePlaceholder")}
                        className="h-8 text-xs flex-1 min-w-[100px]"
                      />
                    )}
                    {ifState.conds.length > 1 && (
                      <Button
                        type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                        onClick={() => applyIf({ conds: ifState.conds.filter((_, idx) => idx !== i) })}
                        aria-label={t("forms.customField.removeCondition")}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs"
                onClick={() => applyIf({ conds: [...ifState.conds, { field: "", op: "=", value: "" }] })}
              >
                {t("forms.customField.addCondition")}
              </Button>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.then")}</label>
                  <Input
                    value={ifState.then}
                    onChange={e => applyIf({ then: e.target.value })}
                    placeholder={t("forms.customField.thenPlaceholder")}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.else")}</label>
                  <Input
                    value={ifState.else}
                    onChange={e => applyIf({ else: e.target.value })}
                    placeholder={t("forms.customField.elsePlaceholder")}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
              <div className="rounded-md border border-input bg-muted/40 px-2 py-1.5 text-xs font-mono break-words min-h-[32px]">
                {serializeIf(ifState) || <span className="text-muted-foreground">{t("forms.customField.completeConditions")}</span>}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("forms.customField.numericNote")}
              </p>
            </div>
          ) : isVariadicKind(fKind) ? (
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.fields")}</label>
              <div className="space-y-1.5">
                {fArgs.map((arg, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <FormulaFieldToken
                      value={arg}
                      placeholder={t("forms.customField.fieldPlaceholder")}
                      // COUNT works on any field (it counts filled ones);
                      // SUM/AVERAGE only make sense over numbers.
                      fields={pickFormulaFields(formulaFields, fKind === "count" ? undefined : NUMERIC_TYPES)}
                      onSelect={(v) => setArg(i, v)}
                      emptyLabel={fKind === "count" ? undefined : t("forms.customField.noNumberFields")}
                    />
                    {fArgs.length > 1 && (
                      <Button
                        type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                        onClick={() => removeArg(i)}
                        aria-label={t("forms.customField.removeField")}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs"
                onClick={addArg}
              >
                {t("forms.customField.addField")}
              </Button>
              <div className="rounded-md border border-input bg-muted/40 px-2 py-1.5 text-xs font-mono break-words min-h-[32px]">
                {builtFormula || <span className="text-muted-foreground">{t("forms.customField.pickOneField")}</span>}
              </div>
            </div>
          ) : fKind === "lookup" ? (
            <div className="space-y-2">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.relatedRecord")}</label>
                <Select value={fSlots.a ?? ""} onValueChange={setLookupRelation}>
                  <SelectTrigger className="h-9 text-sm" data-testid="formula-relation-select">
                    <SelectValue placeholder={t("forms.customField.selectRelatedRecord")} />
                  </SelectTrigger>
                  <SelectContent>
                    {lookupRelations.map(r => (
                      <SelectItem key={r.relation} value={r.relation} className="text-sm">{formulaRelationLabel(r, t)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.fieldToPull")}</label>
                <div className="min-h-[36px] rounded-md border border-input bg-background px-2 py-1.5 text-sm font-mono leading-7 break-words">
                  <FormulaFieldToken
                    value={fSlots.b ?? ""}
                    placeholder={t("forms.customField.fieldPlaceholder")}
                    fields={lookupTargetFields}
                    onSelect={(v) => setSlot("b", v)}
                    emptyLabel={
                      activeRelation
                        ? t("forms.customField.noFieldsOnRelation", { relation: activeRelation.label })
                        : t("forms.customField.selectRelationFirst")
                    }
                  />
                </div>
              </div>
              <div className="rounded-md border border-input bg-muted/40 px-2 py-1.5 text-xs font-mono break-words min-h-[32px]">
                {builtFormula || <span className="text-muted-foreground">{t("forms.customField.pickRelationAndField")}</span>}
              </div>
            </div>
          ) : (
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.expression")}</label>
            <div className="min-h-[72px] rounded-md border border-input bg-background px-2 py-2 text-sm font-mono leading-7 break-words">
              {formulaSegments(fKind).map((seg, i) =>
                "lit" in seg ? (
                  <span key={i} className="text-muted-foreground whitespace-pre">{seg.lit}</span>
                ) : seg.kind === "field" ? (
                  <FormulaFieldToken
                    key={i}
                    value={fSlots[seg.slot] ?? ""}
                    placeholder={formulaPlaceholder(seg, t)}
                    fields={
                      seg.dateOnly
                        // Today sits alongside the record's own date fields, which is what
                        // makes "days since X" (DATEDIF(x, Today)) buildable at all.
                        ? withTodayOption(pickFormulaFields(formulaFields, DATE_TYPES))
                        : pickFormulaFields(
                            formulaFields, seg.numeric ? NUMERIC_TYPES : undefined)
                    }
                    onSelect={(v) => setSlot(seg.slot, v)}
                    emptyLabel={
                      seg.numeric
                        ? t("forms.customField.noNumberFields")
                        : seg.dateOnly
                          ? t("forms.customField.noDateFields")
                          : undefined
                    }
                  />
                ) : (
                  <FormulaValueToken
                    key={i}
                    value={fSlots[seg.slot] ?? ""}
                    placeholder={formulaPlaceholder(seg, t)}
                    onChange={(v) => setSlot(seg.slot, v)}
                  />
                )
              )}
            </div>
          </div>
          )}
          {unresolvedRefs.length > 0 && (
            <p className="text-xs text-destructive">
              {t("forms.customField.unresolvedRefs", {
                count: unresolvedRefs.length,
                field: unresolvedRefs[0],
              })}
            </p>
          )}
        </div>
      )}
      {needsOptions && (
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.options")}</label>
          <div className="space-y-1.5">
            {(options.length ? options : [""]).map((opt, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <Input
                  value={opt}
                  onChange={e => {
                    const next = [...(options.length ? options : [""])];
                    next[i] = e.target.value;
                    setOptions(next);
                  }}
                  placeholder={t("forms.customField.optionN", { n: i + 1 })}
                  className="text-sm"
                  onKeyDown={e => {
                    if (e.key === "Enter" && opt.trim()) {
                      e.preventDefault();
                      setOptions([...(options.length ? options : [""]), ""]);
                    }
                  }}
                />
                <button
                    type="button"
                    onClick={() => setOptions(options.filter((_, idx) => idx !== i))}
                    className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground transition shrink-0"
                    aria-label={t("forms.customField.removeOption")}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setOptions([...(options.length ? options : [""]), ""])}
            className="text-xs text-accent hover:underline mt-1"
          >
            {t("forms.customField.addOption")}
          </button>
        </div>
      )}
      {isTrafficLight && (
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">{t("forms.customField.labels")}</label>
          <div className="space-y-1.5">
            {TRAFFIC_LIGHT_COLORS.map((color, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={cn("h-3 w-3 rounded-full shrink-0", color)} />
                <Input
                  value={options[i] ?? ""}
                  onChange={e => {
                    const next = [
                      options[0] ?? TRAFFIC_LIGHT_DEFAULT_LABELS[0],
                      options[1] ?? TRAFFIC_LIGHT_DEFAULT_LABELS[1],
                      options[2] ?? TRAFFIC_LIGHT_DEFAULT_LABELS[2],
                    ];
                    next[i] = e.target.value;
                    setOptions(next);
                  }}
                  placeholder={TRAFFIC_LIGHT_DEFAULT_LABELS[i]}
                  className="text-sm"
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground pt-0.5">
            {t("forms.customField.trafficLightHelp")}
          </p>
        </div>
      )}
      <div className="sticky bottom-0 -mx-3 -mb-3 mt-1 flex justify-end gap-2 border-t border-border bg-popover px-3 py-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>{t("common.cancel")}</Button>
        <Button size="sm" onClick={onSubmit} disabled={!canSubmit}>
          {submitLabel ?? (mode === "add" ? t("forms.customField.create") : t("common.save"))}
        </Button>
      </div>
    </div>
  );
}